import { Core } from 'cytoscape';
import { Project, Station, UndoAction } from '../types';

export interface StationCreationService {
  createStation(position: { x: number; y: number }): Promise<Station>;
  validatePosition(position: { x: number; y: number }): boolean;
  generateStationNumber(): StationNumberInfo;
}

interface StationNumberInfo {
  number: number;
  shouldRenumber: boolean;
  newNumbers: Map<string, number> | null;
}

class StationCreationQueue {
  private queue: Promise<any> = Promise.resolve();

  async enqueue<T>(operation: () => Promise<T>): Promise<T> {
    this.queue = this.queue.then(operation);
    return this.queue;
  }
}

export class StationCreationServiceImpl implements StationCreationService {
  private queue = new StationCreationQueue();
  private lastUsedNumber = 0;

  constructor(
    private project: Project,
    private onProjectChange: (updater: (project: Project) => Project) => void,
    private cyRef: React.MutableRefObject<Core | undefined>
  ) {}

  validatePosition(position: { x: number; y: number }): boolean {
    // Validate position is within bounds
    if (!this.cyRef.current) return false;
    
    const bounds = this.cyRef.current.extent();
    return (
      position.x >= bounds.x1 &&
      position.x <= bounds.x2 &&
      position.y >= bounds.y1 &&
      position.y <= bounds.y2
    );
  }

  generateStationNumber(): StationNumberInfo {
    // Get all existing station numbers and filter out NaN values
    const existingNumbers = this.project.network.stations
      .map(s => s.number)
      .filter(n => !isNaN(n) && n > 0);
    
    // If we have no valid numbers, start from 1
    if (existingNumbers.length === 0) {
      return {
        number: 1,
        shouldRenumber: false,
        newNumbers: null
      };
    }

    const maxExisting = Math.max(...existingNumbers);
    
    // If the max number is too high (more than 2x the number of stations), prepare renumbering info
    if (maxExisting > this.project.network.stations.length * 2) {
      // Create map of station IDs to their new sequential numbers
      const newNumbers = new Map<string, number>();
      this.project.network.stations.forEach((station, index) => {
        newNumbers.set(station.id, index + 1);
      });
      
      // Return the next number after renumbering along with renumbering info
      return {
        number: this.project.network.stations.length + 1,
        shouldRenumber: true,
        newNumbers
      };
    }
    
    return {
      number: maxExisting + 1,
      shouldRenumber: false,
      newNumbers: null
    };
  }

  async createStation(position: { x: number; y: number }): Promise<Station> {
    return this.queue.enqueue(async () => {
      // 1. Validate position
      if (!this.validatePosition(position)) {
        throw new Error('Invalid position');
      }

      // 2. Check station limit
      if (this.project.network.stations.length >= 1000) {
        throw new Error('Maximum number of stations reached (1000)');
      }

      // 3. Generate station data and get renumbering info
      const stationNumberInfo = this.generateStationNumber();
      if (isNaN(stationNumberInfo.number)) {
        throw new Error('Failed to generate valid station number');
      }

      const station: Station = {
        id: crypto.randomUUID(),
        number: stationNumberInfo.number,
        name: `City ${stationNumberInfo.number}`,
        population: 0,
        position,
        latitude: null,
        longitude: null,
        population_source: 'manual',
        circle_population: null,
        circle_population_radius: null,
        isDeleted: false
      };

      // 4. Update project state with both renumbering and new station in one update
      this.onProjectChange(project => ({
        ...project,
        lastEdited: Date.now(),
        network: {
          ...project.network,
          stations: [
            ...project.network.stations.map(s => 
              stationNumberInfo.shouldRenumber && stationNumberInfo.newNumbers
                ? { ...s, number: stationNumberInfo.newNumbers.get(s.id)! }
                : s
            ),
            station
          ]
        }
      }));

      // 5. Update visualization
      if (this.cyRef.current) {
        try {
          // Update numbers in visualization if renumbering
          if (stationNumberInfo.shouldRenumber && stationNumberInfo.newNumbers) {
            for (const [stationId, newNumber] of stationNumberInfo.newNumbers) {
              const node = this.cyRef.current.getElementById(stationId);
              if (node) {
                node.data('number', newNumber.toString());
              }
            }
          }

          // Add new station to visualization
          this.cyRef.current.add({
            group: 'nodes',
            data: {
              id: station.id,
              number: station.number.toString(),
              label: station.name,
              name: station.name
            },
            position: station.position
          });
        } catch (error) {
          // If visualization fails, rollback project state
          this.onProjectChange(project => ({
            ...project,
            network: {
              ...project.network,
              stations: project.network.stations.filter(s => s.id !== station.id)
            }
          }));
          throw error;
        }
      }

      return station;
    });
  }

  private async removeStation(id: string): Promise<void> {
    return this.queue.enqueue(async () => {
      // Remove from project state
      this.onProjectChange(project => ({
        ...project,
        lastEdited: Date.now(),
        network: {
          ...project.network,
          stations: project.network.stations.filter(s => s.id !== id),
          routes: project.network.routes.filter(r => r.source !== id && r.target !== id)
        }
      }));

      // Remove from visualization
      if (this.cyRef.current) {
        this.cyRef.current.remove(`node[id = "${id}"]`);
      }
    });
  }
} 